import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';

import { UiModalCommonButtons } from 'src/components/UI/modal/_buttons';
import { BaseApiErrorType, CommonApiResponseType } from 'src/models/BaseApi';
import { BrandingModel } from 'src/models/Branding/BrandingModel';
import { CommonCurrentPartnerType } from 'src/models/CurrentPartner';
import { FrontConfigurationType } from 'src/models/FrontendConfiguration';
import {
  CurrencyListType,
  LookupCountryListType,
  LookupCountryType,
  LookupRegionsType,
  LookupTimezoneListType,
} from 'src/models/Lookup';
import { SidebarModelType } from 'src/models/Partner/Sidebar';
import { BaseStoreEntityType, baseStoreEntityValue } from 'src/store/types';
import { ExpiredCampaignsResponseType } from 'src/models/Modals/expiredCampaigns';

export type InitialCommonStateType = {
  headerHeight: number; // height of <header>
  addBannersHeight: number; // height of additional banners
  innerWidth: number;
  innerHeight: number;
  screenWidth: number;

  // global modal(s). may be many
  modal: {
    id: string;
    // title?: string;
    header?: string;
    body?: string | React.ReactElement;
    preview?: string;
    footer?: (onHideClick: () => void) => React.ReactElement | null;
    buttonFooter?: Omit<UiModalCommonButtons, 'close'>;
  }[];
  overlayShareLink: string | null;
  showLoader: boolean;
  configuration: BaseStoreEntityType<FrontConfigurationType & { RecaptchaSiteKey: string }>;
  sidebar: BaseStoreEntityType<SidebarModelType>;
  sidebarOpen: boolean;
  organizationSubscriptionType: number;
  currentPartner: BaseStoreEntityType<CommonCurrentPartnerType>;
  brandingModel: BrandingModel;
  brandingModelLoading: boolean;

  percentLoading: number;
  percentErrors: BaseApiErrorType[];

  currencyList: BaseStoreEntityType<CurrencyListType>;
  countriesList: BaseStoreEntityType<LookupCountryListType>;
  regionsMap: {
    loading: boolean;
    error: CommonApiResponseType | null;
    response: Record<LookupCountryType['ID'], LookupRegionsType>;
  };
  timezonesList: BaseStoreEntityType<LookupTimezoneListType>;

  isErrorPageShown: boolean;

  isRallyUpPlusFlagEnabledLoading: boolean;
  isRallyUpPlusFlagEnabled: boolean;

  releaseFeatureFlags: {
    loading: boolean;
    name: string;
    response: boolean | null;
  }[];

  experimentalFeatureFlags: {
    loading: boolean;
    name: string;
    response: boolean | null;
  }[];

  canCreateCampaignFromTemplate: boolean;
  showTemplateGalleryModal: boolean;
  templateGalleryModalCalledFrom: string | null;
  showTemplateGalleryModalDetailsId?: string;

  isCurrentPageSPA: boolean;

  showPersonalOrBusinessForBusinessModal: boolean;
  shouldRedirectToStopPage: boolean;

  expiredCampaignsModal: ExpiredCampaignsResponseType & {
    isShow: boolean;
  };
};

const initialState: InitialCommonStateType = {
  headerHeight: 0,
  addBannersHeight: 0,
  innerWidth: 0,
  innerHeight: 0,
  screenWidth: 0,
  modal: [],
  showLoader: false,
  overlayShareLink: null,
  configuration: baseStoreEntityValue<FrontConfigurationType & { RecaptchaSiteKey: string }>(null, true),
  sidebar: baseStoreEntityValue(),
  sidebarOpen: false,
  organizationSubscriptionType: 1,
  currentPartner: baseStoreEntityValue<CommonCurrentPartnerType>(null, true),
  brandingModel: {} as BrandingModel,
  brandingModelLoading: true,
  percentLoading: 0,
  percentErrors: [],
  currencyList: baseStoreEntityValue<CurrencyListType>(null, true),
  countriesList: baseStoreEntityValue<LookupCountryListType>(null, false),
  regionsMap: {
    loading: false,
    error: null,
    response: {},
  },
  timezonesList: baseStoreEntityValue<LookupTimezoneListType>(),
  isErrorPageShown: false,
  isRallyUpPlusFlagEnabledLoading: true,
  isRallyUpPlusFlagEnabled: false,

  releaseFeatureFlags: [
    { loading: true, name: '17205_OrganizationNotes', response: null },
    { loading: true, name: '21065_SysadminFlags', response: null },
    { loading: true, name: '26794_CampaignSharingImprovements', response: null },
  ],

  experimentalFeatureFlags: [],

  canCreateCampaignFromTemplate: false,
  showTemplateGalleryModal: false,
  templateGalleryModalCalledFrom: null,
  showTemplateGalleryModalDetailsId: undefined,

  isCurrentPageSPA: false,

  showPersonalOrBusinessForBusinessModal: false,
  shouldRedirectToStopPage: false,

  expiredCampaignsModal: {
    CountExpiredSoonCampaign: 0,
    Items: [],
    isShow: false,
  },
};

export const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setCommon: (state, data: PayloadAction<Partial<InitialCommonStateType>>) => {
      state = {
        ...state,
        ...data.payload,
      };

      return state;
    },

    showModal: (state, { payload }: PayloadAction<Omit<InitialCommonStateType['modal'][0], 'id'>>) => {
      state.modal.push({ ...payload, id: Math.random().toString() });
    },
    hideModal: (state, { payload }: PayloadAction<InitialCommonStateType['modal'][0]['id']>) => {
      state.modal = state.modal.filter(({ id }) => id !== payload);
    },

    overlayShareLinkShow: (state, { payload }: PayloadAction<InitialCommonStateType['overlayShareLink']>) => {
      if (payload) {
        state.overlayShareLink = payload;
      }
    },
    overlayShareLinkHide: (state) => {
      state.overlayShareLink = null;
    },

    getConfigurationOk: (state, { payload }: PayloadAction<FrontConfigurationType & { RecaptchaSiteKey: string }>) => {
      state.configuration.response = payload;
      state.configuration.loading = false;
    },
    getConfigurationError: (state, { payload }: PayloadAction<BaseApiErrorType>) => {
      state.configuration.error = payload;
      state.configuration.loading = false;
    },

    getCurrentPartnerOk: (state, { payload }: PayloadAction<CommonCurrentPartnerType>) => {
      state.currentPartner = baseStoreEntityValue(payload, false);
    },
    getCurrentPartnerError: (state, { payload }: PayloadAction<BaseApiErrorType>) => {
      state.currentPartner = baseStoreEntityValue(null, false, payload);
    },
    changeCurrentPartnerData: (state, { payload }: PayloadAction<Partial<CommonCurrentPartnerType>>) => {
      state.currentPartner.response = state.currentPartner.response as CommonCurrentPartnerType;

      state.currentPartner.response = {
        ...state.currentPartner.response,
        ...payload,
      };
    },

    getBrandingModelOk: (state, { payload }: PayloadAction<BrandingModel>) => {
      state.brandingModel = payload;
      state.brandingModelLoading = false;
    },
    getBrandingModelError: () => {
      /**
       * there are default values in the store
       * just for the correct redux flow
       */
    },

    getCurrencyListOk: (state, { payload }: PayloadAction<CurrencyListType>) => {
      state.currencyList = baseStoreEntityValue(payload, false);
    },
    getCurrencyListError: (state, { payload }: PayloadAction<BaseApiErrorType>) => {
      state.currencyList = baseStoreEntityValue(null, false, payload);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getRegionsByCountryIdStart: (state, { payload }: PayloadAction<LookupCountryType['ID']>) => {
      state.regionsMap.loading = true;
      state.regionsMap.error = null;
    },
    getRegionsByCountryIdOk: (
      state,
      { payload }: PayloadAction<{ countryId: LookupCountryType['ID']; data: LookupRegionsType }>
    ) => {
      state.regionsMap.loading = false;
      state.regionsMap.response[payload.countryId] = payload.data;
    },
    getRegionsByCountryIdError: (state, { payload }: PayloadAction<BaseApiErrorType>) => {
      state.regionsMap.loading = false;
      state.regionsMap.error = payload;
    },

    getCountriesListStart: (state) => {
      state.countriesList = baseStoreEntityValue(null, true);
    },
    getCountriesListOk: (state, { payload }: PayloadAction<LookupCountryListType>) => {
      state.countriesList = baseStoreEntityValue(payload, false);
    },
    getCountriesListError: (state, { payload }: PayloadAction<BaseApiErrorType>) => {
      state.countriesList = baseStoreEntityValue(null, false, payload);
    },

    getTimezonesListStart: (state) => {
      state.timezonesList = baseStoreEntityValue(null, true);
    },
    getTimezonesListOk: (state, { payload }: PayloadAction<LookupTimezoneListType>) => {
      state.timezonesList = baseStoreEntityValue(payload, false);
    },
    getTimezonesListError: (state, { payload }: PayloadAction<BaseApiErrorType>) => {
      state.timezonesList = baseStoreEntityValue(null, false, payload);
    },

    toggleLoader: (state, { payload }: PayloadAction<boolean>) => {
      state.showLoader = payload;
    },

    setSidebarModel: (state, { payload }: PayloadAction<SidebarModelType | null>) => {
      state.sidebar.response = payload;
    },
    setSidebarIsOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.sidebarOpen = payload;
    },

    setOrganizationSubscriptionType: (state, { payload }: PayloadAction<number>) => {
      state.organizationSubscriptionType = payload;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    changeOrganizationStart: (state, { payload }: PayloadAction<BaseApiErrorType>) => {
      state.sidebar.loading = true;
    },
    changeOrganizationOk: (state, { payload }: PayloadAction<SidebarModelType>) => {
      state.sidebar.response = payload;
      state.sidebar.loading = false;
    },
    changeOrganizationError: (state, { payload }: PayloadAction<BaseApiErrorType>) => {
      state.sidebar.loading = false;
      state.sidebar.error = payload;
      state.sidebar.response = null;
    },

    changePercentage: (state, { payload }: PayloadAction<{ percent?: number; error?: BaseApiErrorType }>) => {
      if (payload.percent !== undefined) {
        state.percentLoading = payload.percent;
      }
      if (payload.error !== undefined) {
        state.percentErrors.push(payload.error);
      }
    },

    toggleErrorPageShown: (state, { payload }: PayloadAction<boolean>) => {
      state.isErrorPageShown = payload;
    },

    getReleaseFeatureFlagsOk: (state, { payload }: PayloadAction<{ name: string; response: boolean }[]>) => {
      const newState = cloneDeep(state.releaseFeatureFlags);

      payload.forEach((item) => {
        const foundIndex = newState.findIndex(({ name }) => name === item.name);

        if (~foundIndex) {
          newState[foundIndex] = {
            ...item,
            loading: false,
          };
        }
      });

      state.releaseFeatureFlags = newState;
    },
    // nothing to do
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getReleaseFeatureFlagsError: () => {},

    getExperimentalFeatureFlagsOk: (state, { payload }: PayloadAction<{ name: string; response: boolean }[]>) => {
      const newState = cloneDeep(state.experimentalFeatureFlags);

      payload.forEach((item) => {
        const foundIndex = newState.findIndex(({ name }) => name === item.name);

        if (~foundIndex) {
          newState[foundIndex] = {
            ...item,
            loading: false,
          };
        }
      });

      state.experimentalFeatureFlags = newState;
    },
    // nothing to do
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getExperimentalFeatureError: () => {},

    canCreateCampaignFromTemplateOk: (state, { payload }: PayloadAction<boolean>) => {
      state.canCreateCampaignFromTemplate = payload;
    },
    canCreateCampaignFromTemplateError: (state) => {
      state.canCreateCampaignFromTemplate = false;
    },
    showTemplateGalleryModal: (state, { payload }: PayloadAction<string | null>) => {
      state.showTemplateGalleryModal = true;
      state.templateGalleryModalCalledFrom = payload;
    },
    hideTemplateGalleryModal: (state) => {
      state.showTemplateGalleryModalDetailsId = undefined;
      state.showTemplateGalleryModal = false;
    },
    showTemplateGalleryModalDetailsScreen: (state, { payload }: PayloadAction<string>) => {
      state.showTemplateGalleryModalDetailsId = payload;
      state.showTemplateGalleryModal = true;
    },
    cleanTemplateGalleryModalDetailsScreen: (state) => {
      state.showTemplateGalleryModalDetailsId = undefined;
    },

    checkCustomerTypeOk: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldRedirectToStopPage = payload;
    },
    checkCustomerTypeError: (state) => {
      state.shouldRedirectToStopPage = false;
    },
    showPersonalOrBusinessForBusinessModal: (state) => {
      state.showPersonalOrBusinessForBusinessModal = true;
    },
    hidePersonalOrBusinessForBusinessModal: (state) => {
      state.showPersonalOrBusinessForBusinessModal = false;
    },

    // nothing to do
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    checkExpiredCampaignsStart: () => {},
    showExpiredCampaignsModal: (state, { payload }: PayloadAction<ExpiredCampaignsResponseType>) => {
      state.expiredCampaignsModal = {
        ...payload,
        isShow: payload.CountExpiredSoonCampaign > 0,
      };
    },
    hideExpiredCampaignsModal: (state) => {
      state.expiredCampaignsModal.isShow = false;
    },
  },
});

export const {
  showModal,
  hideModal,
  toggleLoader: toggleGlobalLoader,
  showTemplateGalleryModal,
  hideTemplateGalleryModal,
  showTemplateGalleryModalDetailsScreen,
  cleanTemplateGalleryModalDetailsScreen,
} = commonSlice.actions;
